import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Car,
  Bus,
  Navigation,
  Send,
  CheckCircle,
  Accessibility,
  CreditCard,
  ShoppingBag,
  Info,
  Calendar,
  AlertCircle
} from 'lucide-react';
import { pharmacyInfo } from '../mock';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 3000);
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
  };

  const openingHours = [
    { day: 'Lundi - Vendredi', hours: '8h00 - 19h00', isOpen: true },
    { day: 'Samedi', hours: '8h00 - 17h00', isOpen: true },
    { day: 'Dimanche', hours: 'Fermé', isOpen: false }
  ];

  const quickInfo = [
    {
      icon: Accessibility,
      title: "Accessibilité",
      description: "Parking et entrée accessibles aux fauteuils roulants"
    },
    {
      icon: CreditCard,
      title: "Paiements",
      description: "Cartes, espèces et paiement sans contact acceptés"
    },
    {
      icon: ShoppingBag,
      title: "Services",
      description: "Retrait en magasin et livraison gratuite"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-50 via-white to-emerald-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <h1 className="text-5xl font-bold text-gray-900 leading-tight">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-600">Contactez</span>-Nous
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                Notre équipe est à votre disposition pour répondre à vos questions et vous accueillir 
                dans notre pharmacie moderne de Meyrin-Vergers.
              </p>
            </div>

            {/* Quick Info Cards */}
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {quickInfo.map((info, index) => (
                <Card key={index} className="bg-white border-0 shadow-lg">
                  <CardContent className="p-6 text-center">
                    <div className="bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl p-3 w-12 h-12 mx-auto mb-4 flex items-center justify-center">
                      <info.icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">{info.title}</h3>
                    <p className="text-sm text-gray-600">{info.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Informations Pratiques
                </h2>
                
                {/* Contact Details */}
                <div className="space-y-6">
                  <Card className="border-0 shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="bg-teal-100 rounded-lg p-3">
                          <MapPin className="h-6 w-6 text-teal-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-2">Adresse</h3>
                          <p className="text-gray-600">
                            {pharmacyInfo.contact.address.street}<br />
                            {pharmacyInfo.contact.address.postal}<br />
                            <span className="text-teal-600 font-medium">
                              {pharmacyInfo.contact.address.landmark}
                            </span>
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-0 shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="bg-teal-100 rounded-lg p-3">
                          <Phone className="h-6 w-6 text-teal-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-2">Téléphone</h3>
                          <a href={`tel:${pharmacyInfo.contact.phone}`} className="text-teal-600 hover:text-teal-700">
                            {pharmacyInfo.contact.phone}
                          </a>
                          <p className="text-sm text-gray-600 mt-1">
                            Appelez-nous pour vos questions urgentes
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-0 shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="bg-teal-100 rounded-lg p-3">
                          <Mail className="h-6 w-6 text-teal-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
                          <a href={`mailto:${pharmacyInfo.contact.email}`} className="text-teal-600 hover:text-teal-700">
                            {pharmacyInfo.contact.email}
                          </a>
                          <p className="text-sm text-gray-600 mt-1">
                            Pour vos questions non urgentes
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Opening Hours */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Horaires d'Ouverture</h3>
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      {openingHours.map((schedule, index) => (
                        <div key={index} className="flex justify-between items-center">
                          <span className="font-medium text-gray-900">{schedule.day}</span>
                          <span className={`font-medium ${schedule.isOpen ? 'text-teal-600' : 'text-gray-500'}`}>
                            {schedule.hours}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Info className="h-4 w-4" />
                        <span>Horaires susceptibles de changer les jours fériés</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Transportation */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Comment Nous Rejoindre</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <Card className="border-0 shadow-lg">
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-3">
                        <Car className="h-6 w-6 text-teal-600" />
                        <div>
                          <h4 className="font-medium text-gray-900">En Voiture</h4>
                          <p className="text-sm text-gray-600">Parking accessible</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-0 shadow-lg">
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-3">
                        <Bus className="h-6 w-6 text-teal-600" />
                        <div>
                          <h4 className="font-medium text-gray-900">Transports</h4>
                          <p className="text-sm text-gray-600">Arrêts à proximité</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Envoyez-nous un Message
              </h2>
              
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8">
                  {isSubmitted ? (
                    <div className="text-center py-8">
                      <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        Message Envoyé !
                      </h3>
                      <p className="text-gray-600">
                        Nous vous répondrons dans les plus brefs délais.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="name">Nom complet *</Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="phone">Téléphone</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="mt-1"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label htmlFor="subject">Sujet</Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label htmlFor="message">Message *</Label>
                        <Textarea
                          id="message"
                          name="message"
                          rows={4}
                          value={formData.message}
                          onChange={handleInputChange}
                          required
                          className="mt-1"
                        />
                      </div>

                      <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700 text-white" size="lg">
                        <Send className="h-5 w-5 mr-2" />
                        Envoyer le Message
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>

              {/* Emergency Notice */}
              <Card className="mt-6 border-orange-200 bg-orange-50">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="h-6 w-6 text-orange-600 mt-1" />
                    <div>
                      <h3 className="font-semibold text-orange-900 mb-2">
                        Urgences Pharmaceutiques
                      </h3>
                      <p className="text-orange-800 text-sm">
                        Pour les urgences en dehors des heures d'ouverture, 
                        consultez la pharmacie de garde la plus proche ou contactez 
                        le service médical d'urgence.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Notre Emplacement
            </h2>
            <p className="text-xl text-gray-600">
              Idéalement situé face à l'hôpital dans le quartier des Vergers
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="aspect-video bg-gradient-to-br from-teal-100 to-emerald-100 flex items-center justify-center">
              <div className="text-center">
                <MapPin className="h-24 w-24 text-teal-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Pharmacie Meyrin-Vergers
                </h3>
                <p className="text-gray-600 mb-4">
                  1217 Meyrin-Vergers • Face à l'hôpital
                </p>
                <Button className="bg-teal-600 hover:bg-teal-700 text-white">
                  <Navigation className="h-5 w-5 mr-2" />
                  Obtenir l'Itinéraire
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;